import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';
import Select from 'react-select';

const STATUS_META = {
  open: {
    label: 'Open',
    badge: 'border border-amber-200 bg-amber-50 text-amber-700',
  },
  'in-progress': {
    label: 'In Progress',
    badge: 'border border-sky-200 bg-sky-50 text-sky-700',
  },
  resolved: {
    label: 'Resolved',
    badge: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  closed: {
    label: 'Closed',
    badge: 'border border-slate-300 bg-slate-100 text-slate-700',
  },
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

function parseDate(input) {
  if (!input) return null;
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

function truncate(text, limit) {
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, Math.max(0, limit - 1))}…`;
}

function initialsFromName(name) {
  if (!name) return 'NA';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function buildInitialsAvatar(name, gender, size) {
  const initials = initialsFromName(name);
  const isMale = gender && ['male', 'm'].includes(String(gender).toLowerCase());
  const bgColor = isMale ? '#3b82f6' : '#ec4899';
  const textColor = '#ffffff';
  return (
    <div
      className="flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        backgroundColor: bgColor,
        color: textColor,
        fontSize: size * 0.4,
        fontWeight: 600,
      }}
    >
      {initials}
    </div>
  );
}

function formatMessageText(text) {
  if (!text) return '';
  const segments = String(text).split(/(\*[^*]+\*)/g);
  return segments.map((segment, idx) => {
    const isBold = segment.startsWith('*') && segment.endsWith('*');
    const content = isBold ? segment.slice(1, -1) : segment;
    const lines = content.split('\n');
    return (
      <React.Fragment key={idx}>
        {lines.map((line, lineIdx) => (
          <React.Fragment key={`${idx}-${lineIdx}`}>
            {lineIdx > 0 && <br />}
            {isBold ? <strong>{line}</strong> : line}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  });
}

export default function StudentIssue() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [courseFilter, setCourseFilter] = useState('');
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const filterScrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);

  const storedUser = AuthService.getUser();
  const storedUserRole = storedUser?.superAdmin?.staff?.role || storedUser?.superAdmin?.role;
  const storedIsSuperAdmin = Boolean(storedUser?.superAdmin?.superAdminId);
  const isAdmin = storedIsSuperAdmin || storedUserRole === 'Admin' || storedUserRole === 'Administrator';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (statusFilter) {
          params.filter = statusFilter;
        }
        const resp = await axiosInstance.get('/tickets', { params });
        setIssues(resp?.data || []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [statusFilter]);

  useEffect(() => {
    if (issues.length === 0) {
      setSelectedIssueId(null);
      return;
    }
    if (selectedIssueId === null || !issues.some((issue) => issue.id === selectedIssueId)) {
      setSelectedIssueId(issues[0].id);
    }
  }, [issues, selectedIssueId]);

  const courseAndSearchFilteredIssues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return issues.filter((issue) => {
      const matchesCourse = !courseFilter || (issue.courseName || '').toLowerCase() === courseFilter.toLowerCase();
      if (!matchesCourse) return false;
      if (!query) return true;
      const haystack = [
        issue.title,
        issue.description,
        issue.student?.name,
        issue.studentName,
        issue.registrationNumber,
        issue.courseName,
        issue.batchName,
        issue.id,
        (issue.chats || []).map((chat) => chat.message).join(' '),
      ];
      return haystack.some((field) => (field || '').toLowerCase().includes(query));
    });
  }, [issues, searchQuery, courseFilter]);

  const filteredIssues = useMemo(
    () => courseAndSearchFilteredIssues.filter((issue) => !statusFilter || issue.status === statusFilter),
    [courseAndSearchFilteredIssues, statusFilter]
  );

  const courseOptions = useMemo(() => {
    const unique = new Set();
    issues.forEach((issue) => {
      const name = (issue.courseName || '').trim();
      if (name) {
        unique.add(name);
      }
    });
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [issues]);

  useEffect(() => {
    if (filteredIssues.length === 0) {
      setSelectedIssueId(null);
      return;
    }
    const visible = selectedIssueId !== null && filteredIssues.some((issue) => issue.id === selectedIssueId);
    if (!visible) {
      setSelectedIssueId(filteredIssues[0].id);
    }
  }, [statusFilter, searchQuery, filteredIssues, selectedIssueId]);

  const selectedIssue = useMemo(() => {
    if (selectedIssueId === null) return null;
    return issues.find((issue) => issue.id === selectedIssueId) || null;
  }, [issues, selectedIssueId]);

  const counts = useMemo(
    () => ({
      total: courseAndSearchFilteredIssues.length,
      open: courseAndSearchFilteredIssues.filter((issue) => issue.status === 'open').length,
      inProgress: courseAndSearchFilteredIssues.filter((issue) => issue.status === 'in-progress').length,
      resolved: courseAndSearchFilteredIssues.filter((issue) => issue.status === 'resolved').length,
      closed: courseAndSearchFilteredIssues.filter((issue) => issue.status === 'closed').length,
    }),
    [courseAndSearchFilteredIssues]
  );

  const checkScrollButtons = () => {
    if (filterScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollFilters = (direction) => {
    if (filterScrollRef.current) {
      const scrollAmount = 200;
      filterScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const handleResize = () => checkScrollButtons();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedIssueId || sending) return;
    const messageToSend = messageInput.trim();
    setMessageInput('');
    setSending(true);
    try {
      const response = await axiosInstance.post(`/tickets/${encodeURIComponent(selectedIssueId)}/reply`, {
        message: messageToSend,
      });
      if (response?.data) {
        setIssues((prevIssues) =>
          prevIssues.map((issue) =>
            issue.id === selectedIssueId
              ? {
                  ...issue,
                  chats: [...(issue.chats || []), response.data],
                  lastMessage: response.data.message || issue.lastMessage,
                  updatedAt: new Date().toISOString(),
                }
              : issue
          )
        );
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        setMessageInput(messageToSend);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send reply');
      setMessageInput(messageToSend);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedIssueId) return;
    setUpdatingStatus(true);
    try {
      const resp = await axiosInstance.post(`/tickets/${encodeURIComponent(selectedIssueId)}/status`, { status: newStatus });
      const updated = resp?.data?.issue;
      setIssues((prev) =>
        prev.map((issue) =>
          issue.id === selectedIssueId
            ? { ...issue, ...(updated || {}), status: newStatus }
            : issue
        )
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update ticket status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    if (!selectedIssueId) return;
    const current = issues.find((issue) => issue.id === selectedIssueId);
    if (!current || (current.chats && current.chats.length > 0)) {
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setConversationLoading(true);
      try {
        const resp = await axiosInstance.get(`/tickets/${encodeURIComponent(selectedIssueId)}`);
        const ticket = resp?.data;
        if (!ticket || cancelled) return;
        setIssues((prev) =>
          prev.map((issue) =>
            issue.id === selectedIssueId
              ? {
                  ...issue,
                  ...ticket,
                  studentName: ticket.studentName || issue.student?.name || issue.studentName,
                  chats: ticket.conversation || [],
                }
              : issue
          )
        );
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to load ticket details');
        }
      } finally {
        if (!cancelled) {
          setConversationLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedIssueId, issues]);

  return (
    <DashboardLayout title="Student Issues">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Student Issues</h1>
          <p className="text-sm text-slate-500">Chat with students and track issue status.</p>
        </div>
        {loading && <span className="text-sm text-slate-500">Loading…</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="flex h-[calc(100vh-9rem)] overflow-hidden rounded-2xl border border-slate-200 bg-[#f0f2f5] shadow-sm">
        <aside className="flex w-full flex-col border-r border-slate-300 bg-white lg:w-[420px]">
          <div className="flex-shrink-0 border-b border-slate-200 bg-[#f0f2f5] px-4 py-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Student Issues</h2>
              <span className="rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white">{counts.total}</span>
            </div>
          </div>

          <div className="flex-shrink-0 border-b border-slate-200 bg-white px-3 py-2">
            <div className="relative">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search conversations"
                className="w-full rounded-lg border-none bg-[#f0f2f5] py-2 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {isAdmin && (
            <div className="flex-shrink-0 border-b border-slate-200 bg-white px-3 py-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Filter by course</label>
              <Select
                isClearable
                placeholder="All courses"
                className="react-select-container text-sm"
                classNamePrefix="react-select"
                options={courseOptions}
                value={courseOptions.find((opt) => opt.value === courseFilter) || null}
                onChange={(option) => setCourseFilter(option ? option.value : '')}
              />
            </div>
          )}

          <div className="flex-shrink-0 border-b border-slate-200 bg-white px-3 py-2">
            <div className="relative flex items-center">
              {showLeftArrow && (
                <button
                  type="button"
                  onClick={() => scrollFilters('left')}
                  className="absolute left-0 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  aria-label="Scroll left"
                >
                  <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              <div
                ref={filterScrollRef}
                onScroll={checkScrollButtons}
                className="flex gap-1 overflow-x-auto scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {[
                  { label: 'Open', value: 'open', count: counts.open },
                  { label: 'In Progress', value: 'in-progress', count: counts.inProgress },
                  { label: 'Resolved', value: 'resolved', count: counts.resolved },
                  { label: 'Closed', value: 'closed', count: counts.closed },
                ].map((filter) => {
                  const isActive = statusFilter === filter.value;
                  return (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setStatusFilter(filter.value)}
                      className={`flex-shrink-0 rounded-md px-4 py-2 text-sm font-medium transition ${
                        isActive ? 'bg-[#ea580c] text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>

              {showRightArrow && (
                <button
                  type="button"
                  onClick={() => scrollFilters('right')}
                  className="absolute right-0 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  aria-label="Scroll right"
                >
                  <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-white">
            {filteredIssues.length === 0 ? (
              <div className="flex h-full items-center justify-center px-6 py-10 text-center">
                <div className="text-sm text-slate-500">
                  <svg className="mx-auto mb-3 h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>No conversations found</p>
                </div>
              </div>
            ) : (
              <div>
                {filteredIssues.map((issue) => {
                  const isActive = issue.id === selectedIssueId;
                  const displayName = issue.student?.name || issue.studentName || issue.registrationNumber || issue.id;
                  const avatarSrc = issue.student?.imagePath || issue.studentImagePath
                    ? getImageURL(issue.student?.imagePath || issue.studentImagePath)
                    : null;
                  const preview = truncate(issue.lastMessage || issue.description, 65);
                  const unreadCount = 0;

                  return (
                    <button
                      key={issue.id}
                      type="button"
                      onClick={() => setSelectedIssueId(issue.id)}
                      className={`group w-full border-b border-slate-100 px-4 py-3 text-left transition hover:bg-[#f5f6f6] ${
                        isActive ? 'bg-[#f0f2f5]' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative flex-shrink-0">
                          {avatarSrc ? (
                            <img
                              src={avatarSrc}
                              alt={displayName}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            buildInitialsAvatar(displayName, issue.student?.gender, 48)
                          )}
                          {unreadCount > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-semibold text-white">
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <h3 className="truncate text-[15px] font-medium text-slate-900">{displayName}</h3>
                            <time className="flex-shrink-0 text-xs text-slate-500">
                              {(() => {
                                const date = parseDate(issue.updatedAt || issue.createdAt);
                                if (!date) return '—';
                                const today = new Date();
                                const isToday = date.toDateString() === today.toDateString();
                                if (isToday) {
                                  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(date);
                                }
                                return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(date);
                              })()}
                            </time>
                          </div>
                          <div className="mt-0.5 flex items-center gap-2">
                            <p className={`flex-1 truncate text-[13px] ${unreadCount > 0 ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                              {issue.title}
                            </p>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-slate-500">{preview}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {selectedIssue ? (
          <main className="flex flex-1 flex-col bg-[#efeae2]">
            <header className="flex-shrink-0 border-b border-slate-300 bg-[#f0f2f5] px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  {(() => {
                    const displayName = selectedIssue.student?.name || selectedIssue.studentName || selectedIssue.registrationNumber || selectedIssue.id;
                    const studentImagePath = selectedIssue.student?.imagePath || selectedIssue.studentImagePath;
                    if (studentImagePath) {
                      return (
                        <img
                          src={getImageURL(studentImagePath)}
                          alt={displayName}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      );
                    }
                    return buildInitialsAvatar(displayName, selectedIssue.student?.gender, 48);
                  })()}
                  <div className="flex-1">
                    <h2 className="text-[15px] font-semibold text-slate-900">
                      {selectedIssue.student?.name || selectedIssue.studentName || selectedIssue.registrationNumber || selectedIssue.id}
                    </h2>
                    <div className="mt-1 space-y-0.5 text-xs text-slate-600">
                      {selectedIssue.registrationNumber && (
                        <p className="flex items-center gap-1">
                          <span className="font-medium">Reg. No:</span>
                          <span>{selectedIssue.registrationNumber}</span>
                        </p>
                      )}
                    </div>
                    <p className="mt-1 text-xs font-medium text-slate-700">{selectedIssue.title}</p>
                    {(selectedIssue.courseName || selectedIssue.batchName) && (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {[selectedIssue.courseName, selectedIssue.batchName].filter(Boolean).join(' • ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <select
                    value={selectedIssue.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updatingStatus}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const descriptor = STATUS_META[selectedIssue.status];
                    if (!descriptor) {
                      return (
                        <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                          {selectedIssue.status}
                        </span>
                      );
                    }
                    return (
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${descriptor.badge}`}>
                        {descriptor.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto bg-[#efeae2] px-4 py-4 sm:px-8 lg:px-16">
              <div className="space-y-2">
                <div className="flex items-start justify-start gap-2">
                  <div className="flex-shrink-0">
                    {(() => {
                      const displayName = selectedIssue.student?.name || selectedIssue.studentName || selectedIssue.registrationNumber || 'S';
                      const studentImagePath = selectedIssue.student?.imagePath || selectedIssue.studentImagePath;
                      if (studentImagePath) {
                        return (
                          <img
                            src={getImageURL(studentImagePath)}
                            alt={displayName}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        );
                      }
                      return buildInitialsAvatar(displayName, selectedIssue.student?.gender, 32);
                    })()}
                  </div>
                  <div className="group max-w-[65%]">
                    <div className="relative rounded-lg bg-white px-3 py-2 shadow-sm">
                      <p className="whitespace-pre-wrap break-words text-[14.2px] leading-[19px] text-slate-900">
                        {selectedIssue.description}
                      </p>
                      <div className="mt-1 flex items-center justify-end gap-1 text-slate-500">
                        <time className="text-[11px]">
                          {(() => {
                            const date = parseDate(selectedIssue.createdAt);
                            if (!date) return '';
                            const dateStr = new Intl.DateTimeFormat('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }).format(date);
                            const timeStr = new Intl.DateTimeFormat('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            }).format(date);
                            return `${dateStr}, ${timeStr}`;
                          })()}
                        </time>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedIssue.chats && selectedIssue.chats.length > 0 ? (
                  selectedIssue.chats.map((chat) => {
                    const isStaffMessage = chat.sender !== 'student';
                    const senderName = chat.senderName || (isStaffMessage ? 'Staff' : null) ||
                      selectedIssue.studentName || selectedIssue.student?.name || selectedIssue.registrationNumber || 'Student';
                    const timestamp = (() => {
                      const date = parseDate(chat.timestamp);
                      if (!date) {
                        return new Intl.DateTimeFormat('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        }).format(new Date());
                      }
                      const dateStr = new Intl.DateTimeFormat('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      }).format(date);
                      const timeStr = new Intl.DateTimeFormat('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      }).format(date);
                      return `${dateStr}, ${timeStr}`;
                    })();

                    if (isStaffMessage) {
                      return (
                        <div key={chat.id} className="flex items-end justify-end gap-2">
                          <div className="group max-w-[65%] flex flex-col items-end">
                            <p className="mb-1 px-2 text-xs font-medium text-slate-600">
                              {senderName}
                            </p>
                            <div className="relative rounded-lg bg-[#d9fdd3] px-3 py-2 shadow-sm">
                              <p className="whitespace-pre-wrap break-words text-[14.2px] leading-[19px] text-slate-900">
                                {formatMessageText(chat.message)}
                              </p>
                              <div className="mt-2 flex items-center justify-end gap-1 text-slate-600">
                                <time className="text-xs font-medium">{timestamp}</time>
                                <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {(() => {
                              const staffImagePath = chat.senderStaffImage || chat.senderSuperAdminImage || null;
                              if (staffImagePath) {
                                return (
                                  <img
                                    src={getImageURL(staffImagePath)}
                                    alt={senderName}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                );
                              }
                              return buildInitialsAvatar(senderName, null, 32);
                            })()}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={chat.id} className="flex items-end justify-start gap-2">
                        <div className="flex-shrink-0">
                          {(() => {
                            const studentImagePath = selectedIssue.student?.imagePath || selectedIssue.studentImagePath;
                            if (studentImagePath) {
                              return (
                                <img
                                  src={getImageURL(studentImagePath)}
                                  alt={senderName}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              );
                            }
                            return buildInitialsAvatar(
                              senderName,
                              selectedIssue.student?.gender,
                              32
                            );
                          })()}
                        </div>
                        <div className="group max-w-[65%]">
                          <div className="relative rounded-lg bg-white px-3 py-2 shadow-sm">
                            <p className="whitespace-pre-wrap break-words text-[14.2px] leading-[19px] text-slate-900">
                              {formatMessageText(chat.message)}
                            </p>
                            <div className="mt-2 flex items-center justify-end gap-1 text-slate-500">
                              <time className="text-xs font-medium">{timestamp}</time>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-sm text-slate-500">No replies yet</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <footer className="flex-shrink-0 border-t border-slate-300 bg-[#f0f2f5] px-4 py-3">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message"
                    rows={1}
                    className="w-full resize-none rounded-lg border-none bg-white px-4 py-2.5 text-[15px] text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    style={{ maxHeight: '120px', overflowY: messageInput.split('\n').length > 4 ? 'auto' : 'hidden' }}
                    onInput={(e) => {
                      const target = e.target;
                      target.style.height = 'auto';
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    disabled={sending}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || sending}
                  className="rounded-full bg-emerald-500 p-2.5 text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Send message"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                  </svg>
                </button>
              </div>
            </footer>
          </main>
        ) : (
          <div className="flex flex-1 items-center justify-center bg-[#f8f9fa]">
            <div className="text-center">
              <svg className="mx-auto mb-4 h-24 w-24 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="text-lg font-medium text-slate-600">Select a conversation</h3>
              <p className="mt-2 text-sm text-slate-500">Choose a student issue from the list to view the conversation</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
